import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatUnits, type Address, type Chain } from 'viem';
import { useBalance, useChains, usePublicClient, useReadContract } from 'wagmi';
import { factoryAbi } from '../abi/factory';
import { splitterHistoryEventsAbi } from '../abi/splitterHistoryEvents';
import { factoryByChainId, getFactoryAddress } from '../config/factories';

const LOG_SCAN_CHUNK_SIZE = 40;

type FundsReceivedTotals =
  | { status: 'ok'; fundsReceived: bigint }
  | { status: 'rate_limited' }
  | { status: 'unavailable' };

function formatNativeAmount(value: bigint, decimals: number): string {
  const asString = formatUnits(value, decimals);
  const [whole, frac = ''] = asString.split('.');
  if (!frac) return whole;
  const trimmed = frac.slice(0, 6).replace(/0+$/g, '');
  return trimmed ? `${whole}.${trimmed}` : whole;
}

function chunkAddresses(list: Address[], size: number): Address[][] {
  const chunks: Address[][] = [];
  for (let i = 0; i < list.length; i += size) chunks.push(list.slice(i, i + size));
  return chunks;
}

function isRateLimitError(err: unknown): boolean {
  const msg = String(err ?? '').toLowerCase();
  return msg.includes('429') || msg.includes('rate limit') || msg.includes('too many requests');
}

function ChainStatsRow({ chain }: { chain: Chain }) {
  const factory = getFactoryAddress(chain.id);
  const publicClient = usePublicClient({ chainId: chain.id });
  const explorerBase = chain.blockExplorers?.default?.url?.replace(/\/$/, '');
  const factoryExplorerHref = factory && explorerBase ? `${explorerBase}/address/${factory}` : undefined;

  const chainNameCell = (
    <td className="font-medium">
      {factoryExplorerHref ? (
        <a href={factoryExplorerHref} target="_blank" rel="noopener noreferrer" className="link link-hover">
          {chain.name}
        </a>
      ) : (
        chain.name
      )}
    </td>
  );

  const {
    data: splitterCount,
    isLoading: splitterCountLoading,
    error: splitterCountError,
  } = useReadContract({
    chainId: chain.id,
    address: factory,
    abi: factoryAbi,
    functionName: 'splitterCount',
    query: { enabled: Boolean(factory) },
  });

  const {
    data: protocolFeeRecipient,
    isLoading: feeRecipientLoading,
    error: feeRecipientError,
  } = useReadContract({
    chainId: chain.id,
    address: factory,
    abi: factoryAbi,
    functionName: 'protocolFeeRecipient',
    query: { enabled: Boolean(factory) },
  });

  const {
    data: feeRecipientBalance,
    isLoading: feeBalanceLoading,
    error: feeBalanceError,
  } = useBalance({
    chainId: chain.id,
    address: protocolFeeRecipient,
    query: { enabled: Boolean(protocolFeeRecipient) },
  });

  const { data: splitters, isLoading: splittersLoading, error: splittersError } = useReadContract({
    chainId: chain.id,
    address: factory,
    abi: factoryAbi,
    functionName: 'getSplitters',
    query: { enabled: Boolean(factory) },
  });

  const {
    data: fundsTotals = { status: 'ok', fundsReceived: BigInt(0) } as FundsReceivedTotals,
    isLoading: fundsTotalsLoading,
  } = useQuery({
    queryKey: [
      'factory-native-totals',
      chain.id,
      factory,
      splitters?.length ?? 0,
    ],
    enabled: Boolean(factory && publicClient && splitters),
    queryFn: async () => {
      if (!publicClient || !splitters || splitters.length === 0) {
        return { status: 'ok', fundsReceived: BigInt(0) } as FundsReceivedTotals;
      }
      try {
        const splitterAddresses = splitters as Address[];
        const chunks = chunkAddresses(splitterAddresses, LOG_SCAN_CHUNK_SIZE);
        let fundsReceived = BigInt(0);

        for (const addressesChunk of chunks) {
          const fundsInLogs = await publicClient.getLogs({
            address: addressesChunk,
            event: splitterHistoryEventsAbi[0],
            fromBlock: BigInt(0),
            toBlock: 'latest',
          });
          fundsReceived += fundsInLogs.reduce((sum, log) => sum + (log.args.amount ?? BigInt(0)), BigInt(0));
        }

        return { status: 'ok', fundsReceived } as FundsReceivedTotals;
      } catch (err) {
        if (isRateLimitError(err)) return { status: 'rate_limited' } as FundsReceivedTotals;
        return { status: 'unavailable' } as FundsReceivedTotals;
      }
    },
    staleTime: 60_000,
    retry: false,
  });

  const rowError = splitterCountError ?? splittersError;
  const loading = Boolean(
    factory &&
      (splitterCountLoading ||
        splittersLoading ||
        feeRecipientLoading ||
        (splitters !== undefined && splitters.length > 0 && fundsTotalsLoading))
  );

  if (!factory) {
    return (
      <tr>
        {chainNameCell}
        <td className="font-mono">-</td>
        <td className="font-mono">-</td>
        <td className="font-mono">-</td>
      </tr>
    );
  }

  if (rowError) {
    return (
      <tr>
        {chainNameCell}
        <td className="font-mono">Error</td>
        <td className="font-mono text-error text-xs break-all">Could not load</td>
        <td className="font-mono text-error text-xs break-all">Could not load</td>
      </tr>
    );
  }

  if (loading) {
    return (
      <tr>
        {chainNameCell}
        <td className="font-mono">Loading...</td>
        <td className="font-mono">Loading...</td>
        <td className="font-mono">Loading...</td>
      </tr>
    );
  }

  return (
    <tr>
      {chainNameCell}
      <td className="font-mono">{splitterCount != null ? (splitterCount + BigInt(1)).toString() : '1'}</td>
      {feeRecipientError || feeBalanceError ? (
        <td className="font-mono opacity-60">Unavailable</td>
      ) : feeBalanceLoading || !protocolFeeRecipient ? (
        <td className="font-mono">Loading...</td>
      ) : (
        <td className="font-mono">
          {formatNativeAmount(feeRecipientBalance?.value ?? BigInt(0), chain.nativeCurrency.decimals)}{' '}
          {chain.nativeCurrency.symbol}
        </td>
      )}
      {fundsTotals.status === 'ok' ? (
        <>
          <td className="font-mono">
            {formatNativeAmount(fundsTotals.fundsReceived, chain.nativeCurrency.decimals)} {chain.nativeCurrency.symbol}
          </td>
        </>
      ) : fundsTotals.status === 'rate_limited' ? (
        <td className="font-mono text-warning">Rate limited</td>
      ) : (
        <td className="font-mono opacity-60">Unavailable</td>
      )}
    </tr>
  );
}

export function FactoryStats() {
  const chains = useChains();
  const statsChains = useMemo(
    () => chains.filter((c) => Object.prototype.hasOwnProperty.call(factoryByChainId, c.id)),
    [chains]
  );

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Factory stats</h1>
        <p className="text-sm opacity-70 mt-1">
          Internal page. Fees earned is shown as the current native balance of each factory&apos;s protocol fee recipient
          wallet. Total funds sent to splitters is summed from on-chain PaymentReceived events.
        </p>
      </div>

      <div className="overflow-x-auto rounded-lg border border-neutral-700/60">
        <table className="table table-zebra w-full">
          <thead>
            <tr>
              <th>Chain</th>
              <th>Splitter contracts created</th>
              <th>Fee wallet balance</th>
              <th>Total funds sent to splitters</th>
            </tr>
          </thead>
          <tbody>
            {statsChains.map((chain) => (
              <ChainStatsRow key={chain.id} chain={chain} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
